import { camelCase } from 'lodash-es';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import BooksCheckInput, {
  InputType,
} from '../../../../components/books/model1/forms/BooksCheckInput';
import BooksTextInput, {
  TextInputType,
} from '../../../../components/books/model1/forms/BooksTextInput';
import * as ICustomFields from '../../../../interfaces/merchants/bookings/custom_fields';
import * as IBooks from '../../../../interfaces/merchants/resources/books';
import ErrorMessage from './InputUserInfoErrorMessage';

interface Props {
  customerFields: IBooks.CustomerFields;
  onChange: (name: string, value: string) => void;
  namePrefix: 'customerFields';
  values: { [name: string]: string };
}

const DEFINED_INPUT_TYPES: { [name: string]: TextInputType } = {
  address: TextInputType.Text,
  membership_number: TextInputType.Text,
  phone_number: TextInputType.Tel,
  zip_code: TextInputType.Tel, // number制御が辛いのでとりあえずtelにしておく
};

const FIELDS_FOR_NUMERIC_WITH_HYPHEN = ['phone_number', 'zip_code'];

const toLabel = (field: IBooks.CustomerField, intl: ReactIntl.InjectedIntl) => {
  const name =
    field.customLabel ||
    intl.formatMessage({ id: `books.formsLabel.${camelCase(field.name)}` });
  switch (field.validationType) {
    case ICustomFields.ValidationType.Alphanumeric:
      return `${name} (${intl.formatMessage({
        id: 'books.inputUserInfoCustomerFields.validationLabel.alphanumeric',
      })})`;
    case ICustomFields.ValidationType.Numeric:
      return `${name} (${intl.formatMessage({
        id: 'books.inputUserInfoCustomerFields.validationLabel.numeric',
      })})`;
    case ICustomFields.ValidationType.Katakana:
      return `${name} (${intl.formatMessage({
        id: 'books.inputUserInfoCustomerFields.validationLabel.katakana',
      })})`;
    default:
      return name;
  }
};

const findValue = (
  editable: boolean,
  value?: string,
  defaultValue?: string,
) => {
  if (!editable) {
    // NOTE: editableがfalseの時にはその項目はpostしない仕様。
    //       stateに混ぜないようにするためにdefaultValueはcreateInitialStateで混ぜていないが
    //       表示やsubmittableの判定上入力状態として扱う必要があるので
    //       !editableのときにはdefaultValueを表示するようにする
    return defaultValue || '';
  }
  if (value === '') {
    return '';
  }
  return value;
};

export const submittable = (
  customerFields?: IBooks.CustomerFields,
  values?: { [name: string]: string },
): boolean => {
  if (!customerFields) {
    return true;
  }
  return customerFields.fields.every(f =>
    f.required
      ? !!findValue(f.editable, (values || {})[f.name], f.defaultValue)
      : true,
  );
};

export const createInitialState = (customerFields?: IBooks.CustomerFields) => {
  if (!customerFields) {
    return {};
  }
  return customerFields.fields.reduce((acc, field) => {
    if (!field.editable) {
      return acc;
    }
    acc[field.name] = field.defaultValue || '';
    return acc;
  }, {});
};

class InputUserInfoCustomerFields extends React.Component<
  Props & InjectedIntlProps
> {
  constructor(props: Props & InjectedIntlProps) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.renderInput = this.renderInput.bind(this);
  }

  private handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const {
      currentTarget: { name, value },
    } = e;
    const { namePrefix } = this.props;
    const applyValue =
      FIELDS_FOR_NUMERIC_WITH_HYPHEN.indexOf(name) >= 0
        ? value.replace(/[^0-9\\-]/g, '')
        : value;
    this.props.onChange(`${namePrefix}.${name}`, applyValue);
  }

  private renderInput(
    intl: InjectedIntlProps['intl'],
    field: IBooks.CustomerField,
  ) {
    const { values } = this.props;
    switch (field.name) {
      case 'birthday':
        return (
          <BooksTextInput
            type={TextInputType.Date}
            name={field.name}
            value={findValue(
              field.editable,
              values[field.name],
              field.defaultValue,
            )}
            onChange={this.handleChange}
            disabled={!field.editable}
            setValue={field.defaultValue !== undefined && !field.editable}
          />
        );
      case 'gender':
        return (
          <div>
            <BooksCheckInput
              type={InputType.Radio}
              name={field.name}
              value="female"
              onChange={this.handleChange}
              checked={
                findValue(
                  field.editable,
                  values[field.name],
                  field.defaultValue,
                ) === 'female'
              }
              disabled={!field.editable}
            >
              {intl.formatMessage({ id: `books.formsLabel.female` })}
            </BooksCheckInput>
            <BooksCheckInput
              type={InputType.Radio}
              name={field.name}
              value="male"
              onChange={this.handleChange}
              checked={
                findValue(
                  field.editable,
                  values[field.name],
                  field.defaultValue,
                ) === 'male'
              }
              disabled={!field.editable}
            >
              {intl.formatMessage({ id: `books.formsLabel.male` })}
            </BooksCheckInput>
          </div>
        );
      default:
        return (
          <BooksTextInput
            type={DEFINED_INPUT_TYPES[field.name]}
            name={field.name}
            value={findValue(
              field.editable,
              values[field.name],
              field.defaultValue,
            )}
            onChange={this.handleChange}
            disabled={!field.editable}
            setValue={field.defaultValue !== undefined && !field.editable}
          />
        );
    }
  }

  public render() {
    const { customerFields, intl } = this.props;
    const labelDescription = (type: string, defaultValue?: string) => {
      if (!defaultValue && type === 'email') {
        return intl.formatMessage({ id: 'books.formsDescription.email' });
      } else {
        return undefined;
      }
    };

    return customerFields.fields.map(f => (
      <div key={f.name}>
        <BooksLabeledContainer
          label={toLabel(f, intl)}
          requiredLabel={f.required && f.editable}
          errorStyle={f.errors && f.errors.length !== 0}
          paddingTop={true}
          description={labelDescription(f.name, f.defaultValue)}
        >
          {this.renderInput(intl, f)}
        </BooksLabeledContainer>
        <ErrorMessage fieldSource={f} />
      </div>
    ));
  }
}

export default injectIntl(InputUserInfoCustomerFields);
